// useTableSelection.js
// Shared hook for "select mode" tables across AllLeads, ClientsList, AllBids,
// AllStock, AllSaleOrder, AllInvoice.
//
// Usage:
//   const selection = useTableSelection(rows, row => row.id);
//   selection.isSelectMode         // boolean
//   selection.toggleSelectMode()
//   selection.selectedIds          // Set of row IDs
//   selection.toggleRow(id)
//   selection.selectAll()
//   selection.clearSelection()
//   selection.selectedRows         // array of full row objects that are selected
//   selection.isRowSelected(id)
//   selection.allSelected          // boolean

import { useState, useMemo, useCallback, useEffect } from 'react';

export default function useTableSelection(rows, getId = (r) => r.id) {
    const [isSelectMode, setIsSelectMode] = useState(false);
    const [selectedIds, setSelectedIds] = useState(() => new Set());

    // If the underlying row set changes (filter/search), drop selections that
    // no longer exist so exports never contain phantom rows.
    useEffect(() => {
        if (selectedIds.size === 0) return;
        const validIds = new Set(rows.map(getId));
        let changed = false;
        const next = new Set();
        selectedIds.forEach(id => {
            if (validIds.has(id)) next.add(id);
            else changed = true;
        });
        if (changed) setSelectedIds(next);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rows]);

    const toggleSelectMode = useCallback(() => {
        setIsSelectMode(prev => {
            if (prev) setSelectedIds(new Set());   // exiting select mode clears selection
            return !prev;
        });
    }, []);

    const toggleRow = useCallback((id) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    }, []);

    const selectAll = useCallback(() => {
        setSelectedIds(new Set(rows.map(getId)));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rows]);

    const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

    const isRowSelected = useCallback((id) => selectedIds.has(id), [selectedIds]);

    const selectedRows = useMemo(
        () => rows.filter(r => selectedIds.has(getId(r))),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [rows, selectedIds]
    );

    const allSelected = rows.length > 0 && selectedIds.size === rows.length;

    return {
        isSelectMode,
        toggleSelectMode,
        selectedIds,
        selectedRows,
        toggleRow,
        selectAll,
        clearSelection,
        isRowSelected,
        allSelected,
    };
}